// src/pages/AboutUs.tsx

import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import { Helmet } from 'react-helmet-async';
import ParticleBackground from '../components/layout/ParticleBackground';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDownFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;


const AboutUsContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: transparent;
  color: var(--color-text);
  position: relative;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 6rem auto 4rem;
  padding: 8rem 2rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 6rem 1.5rem;
  }
`;

const Title = styled.h1`
  color: var(--color-text);
  font-size: 3.5rem;
  margin-bottom: 2rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;
  animation: ${slideDownFadeIn} 0.8s ease-out forwards;
  opacity: 0;
  font-family: 'Maragsa-Display', Helvetica, Arial, sans-serif;
  text-align: center;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 3px;
    background: linear-gradient(90deg, transparent, var(--color-primary), transparent);
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
    letter-spacing: 1px;
  }
`;

const Description = styled.div`
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  color: var(--color-text);
  max-width: 100%;
  animation: ${fadeIn} 0.8s ease-out 0.3s forwards;
  opacity: 0;
  font-family: 'Montserrat', Arial, sans-serif;

  p {
    margin-bottom: 1.5rem;
    text-align: justify;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    font-size: 1.1rem;
    line-height: 1.6;
    
    p {
      text-align: left;
    }
  }
`;

interface AboutUsProps {
  isLoggedIn: boolean;
  onLogout: () => void;
}

const AboutUs: React.FC<AboutUsProps> = ({ isLoggedIn, onLogout }) => {

  // Meta Pixel: Track Page View on AboutUs Load
  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'ViewContent', { page: 'AboutUs' });
    }
  }, []);

  return (
    <AboutUsContainer>
      <Helmet>
        <title>About RiskMaster | The Story Behind the Tool</title>
        <meta
          name="description"
          content="Learn about Aviram, the creator of RiskMaster, and how his trading journey led to building a tool that helps over 1500 traders worldwide manage risk and maintain discipline."
        />
        <link rel="canonical" href="https://www.riskmaster.net/about-us" />
      </Helmet>
      <ParticleBackground />
      <Navigation
        activeSection="about-us"
        handleScroll={() => {}}
        isLoggedIn={isLoggedIn}
        onLogout={onLogout}
      />
      <ContentWrapper>
        <Title>About RiskMaster</Title>
        <Description>
          <p>
            My name is Aviram, and I'm a software developer and light designer by trade. I started trading in 2023, 
            focusing on algorithmic trading and manual intraday futures trading using prop firms.
          </p>
          <p>
            When trading manually with my friend Shlomi, I discovered how difficult it was to build and follow my own 
            strategy. I would often tilt, overtrade, add contracts, or completely ignore the rules I set for myself, 
            which led to failing my prop firm accounts.
          </p>
          <p>
            That's when I decided to build a tool that would help us manage our risk automatically. As the developer, 
            I created RiskMaster to enforce discipline and remove the emotional pitfalls that were holding us back.
          </p>
          <p>
            What started as a personal solution for us has now grown into a tool used by over 1500 traders worldwide. 
            RiskMaster helps traders stay consistent with their strategies and avoid the common mistakes that 
            derail so many trading careers.
          </p>
          <p>
            My mission is to provide traders with the tools they need to succeed, removing the emotional barriers 
            that prevent consistent performance. RiskMaster isn't just software—it's the discipline partner 
            every trader needs.
          </p>
        </Description>
      </ContentWrapper>
      <Footer />
    </AboutUsContainer>
  );
};

export default AboutUs;
